/** Dev-tool settings (dev.json, Zod-validated): spawn menu defaults, the Reach folder and panel layout. */
import { z } from 'zod'
import { MISSION_IDS } from '../story/types'
import devJson from './dev.json'
import { gpuTimingConfigSchema } from './gpuTiming'
import { perfSceneConfigSchema } from './perfScene'

export const devConfigSchema = z.object({
  spawnMenu: z
    .object({
      /** `KeyboardEvent.code` that toggles the spawn menu. */
      toggleKey: z.string().min(1),
      /** Default ship def id and AI profile id (fall back to the first available when unknown). */
      ship: z.string().min(1),
      profile: z.string().min(1),
      /** Spawn distance ahead of the player (m). */
      distance: z.object({ value: z.number().positive(), min: z.number().positive(), max: z.number().positive(), step: z.number().positive() }),
      maxWave: z.number().int().min(1),
    })
    .refine((s) => s.distance.min <= s.distance.value && s.distance.value <= s.distance.max, { message: 'distance.value must lie in [min, max]' }),
  /** Career (M2) spawn-menu actions: what they add or spawn. */
  career: z.object({
    ware: z.string().min(1),
    tonnes: z.number().positive(),
    wreckShip: z.string().min(1),
    /** Wreck distance ahead of the player (m). */
    wreckDistance: z.number().positive(),
    credits: z.number().positive(),
    station: z.string().min(1),
  }),
  /** The Reach folder (M4 I8): days the "Run" button advances, and the stats refresh period (s). */
  reach: z.object({
    runDays: z.number().int().min(1),
    statsPollS: z.number().positive(),
  }),
  /** The Fleet folder (M6 P36): the default granted hull, company credits, wing size and the size slider max. */
  fleet: z.object({
    hull: z.string().min(1),
    companyCredits: z.number().positive(),
    wingSize: z.number().int().min(0),
    wingMax: z.number().int().min(1),
    /** M9 CH-12d: how far ahead of the player "Spawn a hulk here" puts the hulk (m). */
    hulkDistanceM: z.number().positive(),
  }),
  /** The Story folder (M7 S36): the default tier, the online days "Pass days" runs, and the default mission. */
  story: z.object({
    tier: z.number().int().min(0).max(5),
    passDays: z.number().int().min(1),
    mission: z.enum(MISSION_IDS),
  }),
  /** PERF G1: the F3 GPU-ms line (timestamp queries only while F3 is open; `gpuTiming.ts`). */
  perfGpu: gpuTimingConfigSchema,
  /** M10 P4b7a: the perf scene (`?perfScene=p4b7` in dev builds; `tests/e2e/ship-perf.spec.ts` stages the same one). */
  perfScene: perfSceneConfigSchema,
  panel: z.object({
    /** Panels scroll beyond this share of the viewport height. */
    maxHeightVh: z.number().min(10).max(100),
    widthPx: z.number().int().min(200).max(800),
  }),
})

export type DevConfig = z.infer<typeof devConfigSchema>

export function loadDevConfig(): DevConfig {
  return devConfigSchema.parse(devJson)
}
